import React, { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  Paper,
  Stack,
  StackProps,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useActionHook } from "hooks";
import { useDealActions, useDealIdSelector } from "store";

import { PopupEmptyState } from "../PopupEmptyState";
import { Form } from "./Form";

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "1px solid #DBE8F5"
}));

interface Props {
  onClose: () => void;
  onCreateSubmit: () => void;
  viewOnly?: boolean;
}

export const History: React.FC<Props> = ({ onClose, onCreateSubmit, viewOnly }) => {
  const dealId = useDealIdSelector();
  const [isCreateEnabled, setIsCreateEnabled] = useState<boolean>(false);

  const handleCloseCreate = () => setIsCreateEnabled(false);

  const { getRatingHistoryAction, createRatingHistoryAction } = useDealActions();

  const { data, loading, action } = useActionHook(getRatingHistoryAction);

  const handleSubmit = async (values: { date: string; provider: string; value: string }) => {
    if (!dealId) return;
    await createRatingHistoryAction({ ...values, deal_id: dealId, rating_date: values.date });
    handleCloseCreate();
    action(dealId);
    onCreateSubmit();
  };

  useEffect(() => {
    if (!dealId) return;
    action(dealId);
  }, [dealId]);

  return (
    <Stack sx={style}>
      {!viewOnly &&
        (isCreateEnabled ? (
          <Form onSubmit={handleSubmit} onCancel={handleCloseCreate} />
        ) : (
          <Button
            onClick={() => setIsCreateEnabled(true)}
            startIcon={<AddIcon />}
            variant="outlined"
            size="sm"
            sx={{ alignSelf: "flex-start" }}
          >
            Add Rating
          </Button>
        ))}

      <AsyncContentMui isLoading={loading}>
        {viewOnly && !data?.rows.length ? (
          <PopupEmptyState text="No rating history for this deal yet." />
        ) : (
          <TableContainer component={Paper} sx={{ border: "1px solid #DBE8F5", borderRadius: "8px", flexGrow: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {data?.columns.map((column) => <TableCell key={column.key}>{column.title}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.rows.map((row, index) => (
                  <TableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    {data?.columns.map((column) => (
                      <StyledTableCell key={index + column.key}>{row[column.key]}</StyledTableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AsyncContentMui>
      <Button variant="outlined" onClick={onClose} sx={{ alignSelf: "flex-end" }}>
        Close
      </Button>
    </Stack>
  );
};

const style: StackProps["sx"] = {
  position: "absolute" as "absolute",
  display: "flex",
  flexDirection: "column",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none",
  bgcolor: "white",
  width: "710px",
  height: "380px",
  p: "32px",
  gap: "20px",
  borderRadius: "8px"
};
